<template>
  <div class="ss-posting-setting mb-6">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start mb-5">
      <div>
        <h4 class="font-weight-bolder text-dark mb-1">Mode Posting SATU SEHAT</h4>
        <p class="text-muted mb-0">
          Pilih posting oleh petugas atau kirim otomatis setiap hari dari server.
        </p>
      </div>
      <div v-if="form.last_automatic_posting_status" class="mt-3 mt-lg-0">
        <span class="badge px-4 py-2" :class="statusBadgeClass">
          {{ statusLabel }}
        </span>
      </div>
    </div>

    <div v-if="loading" class="text-center py-8">
      <b-spinner small variant="primary"></b-spinner>
      <span class="text-muted ml-2">Memuat pengaturan...</span>
    </div>

    <b-form v-else @submit.stop.prevent="save">
      <div class="row">
        <div class="col-lg-6 mb-4 mb-lg-0">
          <label
            class="posting-mode-option"
            :class="{ active: form.posting_mode === 'manual' }"
          >
            <b-form-radio v-model="form.posting_mode" value="manual">
              <span class="font-weight-bolder text-dark">Manual</span>
            </b-form-radio>
            <span class="d-block text-muted mt-2 pl-6">
              Petugas memilih rekam medis dan menekan tombol Posting SATU SEHAT.
            </span>
          </label>
        </div>
        <div class="col-lg-6">
          <label
            class="posting-mode-option"
            :class="{ active: form.posting_mode === 'automatic' }"
          >
            <b-form-radio v-model="form.posting_mode" value="automatic">
              <span class="font-weight-bolder text-dark">Otomatis Harian</span>
            </b-form-radio>
            <span class="d-block text-muted mt-2 pl-6">
              Server mengirim rekam medis yang valid dan belum pernah diposting.
            </span>
          </label>
        </div>
      </div>

      <div v-if="form.posting_mode === 'automatic'" class="automatic-time-panel mt-5">
        <div class="row align-items-end">
          <div class="col-md-5 col-lg-4">
            <b-form-group
              label="Zona waktu"
              label-for="automatic-posting-timezone"
              label-class="font-weight-bolder"
              class="mb-0"
            >
              <b-form-select
                id="automatic-posting-timezone"
                v-model="form.automatic_posting_timezone"
                :options="timezoneOptions"
              ></b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-4 col-lg-3 mt-3 mt-md-0">
            <b-form-group
              label="Jalankan setiap hari pukul"
              label-for="automatic-posting-time"
              label-class="font-weight-bolder"
              class="mb-0"
            >
              <b-form-input
                id="automatic-posting-time"
                v-model="form.automatic_posting_time"
                type="time"
                required
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-3 col-lg-5 mt-3 mt-lg-0">
            <div class="text-muted font-size-sm">
              {{ timezoneDescription }} Data yang belum lengkap tetap ditahan untuk diperbaiki.
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="form.last_automatic_posting_message"
        class="last-run-info mt-5"
      >
        <i class="far fa-clock mr-2"></i>
        <span>{{ form.last_automatic_posting_message }}</span>
        <span v-if="form.last_automatic_posting_finished_at" class="ml-1">
          Terakhir: {{ formatDate(form.last_automatic_posting_finished_at) }}.
        </span>
      </div>

      <div class="d-flex justify-content-end mt-6">
        <b-button type="submit" variant="primary" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-2"></b-spinner>
          <i v-else class="fas fa-save mr-2"></i>
          Simpan Pengaturan
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import moment from "moment";
import module from "@/core/modules/CrudModule.js";

export default {
  data() {
    return {
      loading: true,
      saving: false,
      form: {
        client_id: "",
        client_secret: "",
        organization_id: "",
        posting_mode: "manual",
        automatic_posting_time: "23:00",
        automatic_posting_timezone: "browser",
        automatic_posting_browser_timezone: null,
        last_automatic_posting_status: null,
        last_automatic_posting_message: null,
        last_automatic_posting_finished_at: null,
      },
    };
  },

  computed: {
    timezoneOptions() {
      return [
        {
          value: "browser",
          text: `Waktu Browser (${this.browserTimezone})`,
        },
        { value: "Asia/Jakarta", text: "WIB (UTC+7)" },
        { value: "Asia/Makassar", text: "WITA (UTC+8)" },
        { value: "Asia/Jayapura", text: "WIT (UTC+9)" },
      ];
    },
    browserTimezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Jakarta";
    },
    timezoneDescription() {
      if (this.form.automatic_posting_timezone === "browser") {
        return `Jam mengikuti browser: ${this.browserTimezone}.`;
      }
      const descriptions = {
        "Asia/Jakarta": "Jam mengikuti WIB.",
        "Asia/Makassar": "Jam mengikuti WITA.",
        "Asia/Jayapura": "Jam mengikuti WIT.",
      };
      return descriptions[this.form.automatic_posting_timezone] || "";
    },
    selectedTimezoneLabel() {
      const selected = this.timezoneOptions.find(
        option => option.value === this.form.automatic_posting_timezone
      );
      return selected ? selected.text : this.browserTimezone;
    },
    statusLabel() {
      const labels = {
        running: "Sedang berjalan",
        success: "Otomatisasi berhasil",
        failed: "Otomatisasi gagal",
      };
      return labels[this.form.last_automatic_posting_status] || "Belum dijalankan";
    },
    statusBadgeClass() {
      return {
        "badge-light-info": this.form.last_automatic_posting_status === "running",
        "badge-light-success": this.form.last_automatic_posting_status === "success",
        "badge-light-danger": this.form.last_automatic_posting_status === "failed",
      };
    },
  },

  methods: {
    async load() {
      this.loading = true;
      const data = await module.get(
        `satu-sehat-settings/1?browser_timezone=${encodeURIComponent(this.browserTimezone)}`
      );
      if (data) {
        this.form = Object.assign({}, this.form, data, { _method: "put" });
      }
      this.loading = false;
    },
    async save() {
      this.saving = true;
      this.form.automatic_posting_browser_timezone = this.browserTimezone;
      const response = await module.submit(this.form, "satu-sehat-settings/1");
      this.saving = false;
      if (response.state !== "error") {
        Swal.fire({
          title: "Berhasil",
          text:
            this.form.posting_mode === "automatic"
              ? `Posting otomatis dijadwalkan setiap hari pukul ${this.form.automatic_posting_time} (${this.selectedTimezoneLabel}).`
              : "Posting SATU SEHAT menggunakan mode manual.",
          icon: "success",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3500,
        });
        await this.load();
      }
    },
    formatDate(value) {
      return this.$clinicDateTime(value);
    },
  },

  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.ss-posting-setting {
  border: 1px solid #e4e6ef;
  border-radius: 12px;
  padding: 24px;
  background: #fff;
}

.posting-mode-option {
  display: block;
  height: 100%;
  margin: 0;
  padding: 18px;
  border: 1px solid #e4e6ef;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.posting-mode-option.active {
  border-color: #3699ff;
  background: #f3f8ff;
}

.automatic-time-panel,
.last-run-info {
  padding: 16px 18px;
  border-radius: 8px;
  background: #f3f6f9;
}

.last-run-info {
  color: #5e6278;
  font-size: 13px;
}
</style>
